import type { ReactNode } from "react";

import type { ProjectListItem } from "@/api/hooks";
import { useProjectOverviewQuery } from "@/api/hooks";
import { describeProjectSettings } from "@/lib/projectSettings";
import { formatDate } from "@/lib/format";
import { Dialog, DialogContent } from "@/ui/Dialog";
import { Skeleton } from "@/ui/Skeleton";

type Lineage = ProjectListItem["lineage"];

function Row({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div className="py-2">
      <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {term}
      </dt>
      <dd className="mt-0.5 text-sm text-foreground">{children}</dd>
    </div>
  );
}

/**
 * Read-only facts about how a project was set up, shared by the Projects table
 * and the project header.
 *
 * `lineage` is passed by the table, which already holds it on its row. The
 * overview's `ProjectSummary` carries it too, which is where the header's
 * Details link gets it.
 */
export function ProjectSettingsDialog({
  projectId,
  lineage,
  lastEdited,
  onClose,
}: {
  projectId: string;
  lineage?: Lineage;
  /** Known only to the table row; the overview does not carry it. */
  lastEdited?: string | null;
  onClose: () => void;
}) {
  const overview = useProjectOverviewQuery(projectId);
  const project = overview.data?.project;
  const text = describeProjectSettings({
    tenantPackImport: project?.tenantPackImport,
    lineage: lineage ?? project?.lineage,
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        title="Project settings"
        description="How this project was set up. These facts are read-only."
      >
        {project ? (
          <dl className="divide-y divide-border">
            <Row term="Type">
              {text.type}
              {text.chooseWhen ? (
                <span className="mt-0.5 block text-muted-foreground">
                  {text.chooseWhen}
                </span>
              ) : null}
            </Row>
            {text.startedFrom ? (
              <Row term="Started from">{text.startedFrom}</Row>
            ) : null}
            <Row term="Preview target">{project.previewTenantId}</Row>
            <Row term="Send for review goes to">
              A pack of its own for this project. Other customers and production
              are not changed.
            </Row>
            <Row term="Created">{formatDate(overview.data?.createdAt)}</Row>
            {lastEdited ? (
              <Row term="Last edited">{formatDate(lastEdited)}</Row>
            ) : null}
          </dl>
        ) : overview.isError ? (
          <p role="alert" className="text-sm text-destructive">
            The project settings could not be loaded.
          </p>
        ) : (
          <Skeleton className="h-24 w-full" />
        )}
      </DialogContent>
    </Dialog>
  );
}
